import CloseIcon from '@mui/icons-material/Close';
import Card from '@mui/material/Card';
import IconButton from '@mui/material/IconButton';
import React, { createContext, useState, useCallback, ReactNode, useRef } from 'react';
import './ToasterProvider.scss';
import ToasterWithStatusAndDescription
    from '../../components/toasters/ToasterWithStatusAndDesription/ToasterWithStatusAndDescription';

interface Toaster {
    id: string;
    content: ReactNode;
    closeTime?: number;
}

export interface ToasterWithStatusAndDesriptionParams {
    id: string;
    status: 'success' | 'error' | 'pending';
    description: ReactNode;
    title: string;
    closeTime?: number;
}

interface ToasterContextType {
    addToaster: (id: string, content: ReactNode, closeTime?: number) => void;
    addToasterWithStatusAndDescription: (params: ToasterWithStatusAndDesriptionParams) => void;
    removeToaster: (id: string) => void;
    toasterElements: ReactNode[];
}

export const ToasterContext = createContext<ToasterContextType>({
    addToaster: () => {
    },
    addToasterWithStatusAndDescription: () => {
    },
    removeToaster: () => {},
    toasterElements: []
});

interface ToasterProviderProps {
    children: ReactNode;
}

export const ToasterProvider: React.FC<ToasterProviderProps> = ({ children }) => {
    const [toasters, setToasters] = useState<Toaster[]>([]);
    const timeoutsRef = useRef<{ [key: string]: NodeJS.Timeout }>({});
    const [removingToaster, setRemovingToaster] = useState<string | null>(null);


    const removeToaster = useCallback((id: string) => {
        setRemovingToaster(id);
        setTimeout(() => {
            setToasters((prevToasters) => prevToasters.filter(toaster => toaster.id !== id));
            setRemovingToaster(null);
        }, 300); // Should match the duration of the fadeOut animation
    }, []);

    const addToaster = useCallback((id: string, content: ReactNode, closeTime?: number) => {
        setToasters((prevToasters) => {
            const existingToasterIndex = prevToasters.findIndex(toaster => toaster.id === id);
            if (existingToasterIndex > -1) {
                const updatedToasters = [...prevToasters];
                updatedToasters[existingToasterIndex] = { id, content, closeTime };
                return updatedToasters;
            } else {
                return [...prevToasters, { id, content, closeTime }];
            }
        });

        // Clear existing timeout and set a new one
        if (timeoutsRef.current[id]) {
            clearTimeout(timeoutsRef.current[id]);
        }
        if (closeTime) {
            timeoutsRef.current[id] = setTimeout(() => {
                removeToaster(id);
            }, closeTime);
        }

    }, []);

    const addToasterWithStatusAndDescription = useCallback((params: ToasterWithStatusAndDesriptionParams) => {
        return addToaster(
            params.id,
            <ToasterWithStatusAndDescription title={params.title} status={params.status} description={params.description} />,
            params.closeTime
        );
    }, []);

    const toasterElements = toasters.map(toaster => (
        <Card key={toaster.id} className={`Toaster ${removingToaster === toaster.id ? 'Toaster__exit' : ''}`} variant="outlined">
            {toaster.content}
            <IconButton className="Toaster__close" onClick={() => removeToaster(toaster.id)}>
                <CloseIcon style={{ color: 'white' }} />
            </IconButton>
        </Card>
    ));

    return (
        <ToasterContext.Provider value={{ addToaster, removeToaster, addToasterWithStatusAndDescription, toasterElements }}>
            {children}
        </ToasterContext.Provider>
    );
};
